/**
 * ProductGrid component for displaying products in a responsive grid.
 *
 * Supports loading skeletons, empty state, sale pricing, stock badges,
 * variable product indicators, and pagination.
 *
 * @param {Object}   props
 * @param {Array}    props.products       - Array of product objects.
 * @param {boolean}  props.loading        - Whether products are currently loading.
 * @param {Function} props.onProductClick - Callback fired when a product card is clicked; receives the product.
 * @param {number}   [props.page=1]       - Current page number.
 * @param {number}   [props.totalPages=1] - Total number of pages.
 * @param {Function} [props.onPageChange] - Callback fired when page changes; receives the new page number.
 */
import { createElement, useState } from 'react';
import { formatPrice } from '../../utils/formatPrice';
import VariationModal from './VariationModal';

/* -------------------------------------------------------------------------- */
/*  Skeleton card for loading state                                           */
/* -------------------------------------------------------------------------- */
function SkeletonCard() {
  return (
    <div className="animate-pulse overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Image placeholder */}
      <div className="aspect-square w-full bg-gray-200" />
      {/* Text placeholders */}
      <div className="p-3 space-y-2">
        <div className="h-4 w-3/4 rounded bg-gray-200" />
        <div className="h-3 w-1/2 rounded bg-gray-200" />
        <div className="h-4 w-1/3 rounded bg-gray-200" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Product card                                                              */
/* -------------------------------------------------------------------------- */
function ProductCard({ product, onClick, onStockAdjust }) {
  const isOutOfStock = product.stock_status === 'outofstock';
  const isVariable = product.type === 'variable';
  const isOnSale =
    !isVariable &&
    product.sale_price !== '' &&
    product.sale_price !== null &&
    product.sale_price !== undefined;

  /**
   * For variable products, compute the lowest variation price.
   */
  let lowestVariationPrice = null;
  if (isVariable && Array.isArray(product.variations) && product.variations.length > 0) {
    lowestVariationPrice = product.variations.reduce((min, v) => {
      const p = parseFloat(v.price);
      return !isNaN(p) && (min === null || p < min) ? p : min;
    }, null);
  }

  return (
    <button
      type="button"
      onClick={() => onClick(product)}
      className={
        'group relative flex flex-col overflow-hidden rounded-lg border bg-white ' +
        'text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-pos-primary/30 ' +
        (isOutOfStock ? 'border-gray-200 opacity-60' : 'border-gray-200')
      }
      aria-label={`${product.name}${isOutOfStock ? ' - Out of stock' : ''}`}
    >
      {/* Image */}
      <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          /* Placeholder icon when no image */
          <div className="flex h-full w-full items-center justify-center text-gray-300">
            <svg
              className="h-12 w-12"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}

        {/* Out of stock badge */}
        {isOutOfStock && (
          <span className="absolute left-1.5 top-1.5 rounded bg-pos-danger px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-tight text-white">
            Out of stock
          </span>
        )}

        {/* Sale badge */}
        {isOnSale && !isOutOfStock && (
          <span className="absolute right-1.5 top-1.5 rounded bg-pos-success px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-tight text-white">
            Sale
          </span>
        )}

        {/* Variable product badge */}
        {isVariable && !isOutOfStock && (
          <span className="absolute right-1.5 top-1.5 rounded bg-pos-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-tight text-white">
            Options
          </span>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col p-2.5">
        {/* Product name */}
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-gray-900">
          {product.name}
        </h3>

        {/* SKU */}
        {product.sku && (
          <span className="mt-0.5 text-xs text-gray-400">
            SKU: {product.sku}
          </span>
        )}

        {/* Price */}
        <div className="mt-auto pt-1.5">
          {isVariable ? (
            <span className="text-sm font-semibold text-gray-900">
              {lowestVariationPrice !== null
                ? `From ${formatPrice(lowestVariationPrice)}`
                : formatPrice(parseFloat(product.price) || 0)}
            </span>
          ) : isOnSale ? (
            <span className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400 line-through">
                {formatPrice(parseFloat(product.regular_price))}
              </span>
              <span className="text-sm font-semibold text-pos-danger">
                {formatPrice(parseFloat(product.sale_price))}
              </span>
            </span>
          ) : (
            <span className="text-sm font-semibold text-gray-900">
              {formatPrice(parseFloat(product.price) || 0)}
            </span>
          )}
        </div>

        {/* Stock indicator */}
        {product.manage_stock && product.stock_status !== 'outofstock' && (
          <span
            className={
              'mt-1 text-[11px] font-medium ' +
              (product.stock_quantity <= 5 ? 'text-pos-warning' : 'text-pos-success')
            }
          >
            {product.stock_quantity <= 5
              ? `Only ${product.stock_quantity} left`
              : `${product.stock_quantity} in stock`}
          </span>
        )}
      </div>

      {/* Stock adjust button */}
      {onStockAdjust && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onStockAdjust(product); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onStockAdjust(product); } }}
          className="absolute bottom-1.5 right-1.5 p-1 rounded text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:text-pos-primary hover:bg-pos-primary/10 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
          title="Adjust stock"
          aria-label={`Adjust stock for ${product.name}`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0022 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        </span>
      )}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pagination controls                                                       */
/* -------------------------------------------------------------------------- */
function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  /**
   * Build the list of page numbers to display.
   * Shows first, last, current, and one page on each side of current.
   */
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= page - 1 && i <= page + 1)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  const btnBase =
    'inline-flex h-8 min-w-[2rem] items-center justify-center rounded px-2 text-sm ' +
    'font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-pos-primary/30';

  return (
    <nav className="mt-4 flex items-center justify-center gap-1" aria-label="Pagination">
      {/* Previous button */}
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className={`${btnBase} ${
          page <= 1
            ? 'cursor-not-allowed text-gray-300'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Previous page"
      >
        <svg
          className="h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {/* Page numbers */}
      {pages.map((p, idx) =>
        p === '...' ? (
          <span key={`ellipsis-${idx}`} className="px-1 text-gray-400">
            &hellip;
          </span>
        ) : (
          <button
            type="button"
            key={p}
            onClick={() => onPageChange(p)}
            className={`${btnBase} ${
              p === page
                ? 'bg-pos-primary text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-current={p === page ? 'page' : undefined}
            aria-label={`Page ${p}`}
          >
            {p}
          </button>
        )
      )}

      {/* Next button */}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        className={`${btnBase} ${
          page >= totalPages
            ? 'cursor-not-allowed text-gray-300'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Next page"
      >
        <svg
          className="h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main ProductGrid component                                                */
/* -------------------------------------------------------------------------- */
export default function ProductGrid({
  products = [],
  loading = false,
  onProductClick,
  onStockAdjust,
  page = 1,
  totalPages = 1,
  onPageChange,
}) {
  const [variationProduct, setVariationProduct] = useState(null);

  /**
   * Handle a product card click.
   * For variable products, open the variation modal.
   * For simple products, delegate directly to the parent callback.
   */
  function handleCardClick(product) {
    if (product.type === 'variable') {
      setVariationProduct(product);
    } else {
      onProductClick(product);
    }
  }

  /**
   * When a variation is selected in the modal, pass it up and close.
   */
  function handleVariationSelect(variation) {
    onProductClick(variationProduct, variation);
    setVariationProduct(null);
  }

  /* ---- Loading state ---- */
  if (loading) {
    return (
      <div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  /* ---- Empty state ---- */
  if (!products || products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <svg
          className="mb-4 h-16 w-16 text-gray-300"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
        <p className="text-lg font-medium text-gray-500">No products found</p>
        <p className="mt-1 text-sm text-gray-400">
          Try adjusting your search or category filter.
        </p>
      </div>
    );
  }

  /* ---- Product grid ---- */
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onClick={handleCardClick}
            onStockAdjust={onStockAdjust}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}

      {/* Variation selection modal */}
      {variationProduct && (
        <VariationModal
          product={variationProduct}
          onSelect={handleVariationSelect}
          onClose={() => setVariationProduct(null)}
        />
      )}
    </div>
  );
}
